// Anemoia world layer (custom).
//
// Drives the persistent gallery environment per the brief's baked-video model
// (sec.4 / sec.5.2 / sec.10): the environment is a pre-rendered MP4/WebM loop
// per mode, NOT a live canvas/WebGL scene. This controller only:
//   - crossfades between the SOLAR and LUNAR environment videos on mode change,
//     loading/playing ONLY the active mode's video (lazy, after first paint),
//   - keeps the lightweight ASCII <333 butterflies drifting.
// The sun/moon (CSS) and grain (CSS) need no JS.
//
// It subscribes to the existing mode signal (html[data-anemoia-mode] +
// 'anemoia:mode-change' from anemoia-color-mode.js) and never forks that state.
//
// Performance contract (sec.11, ~80% mobile / <3s):
//   - Video preload="none"; poster shows immediately so LCP isn't blocked.
//   - Only the active mode's video is loaded and playing; the other is paused.
//   - Paused on hidden tab.
//   - prefers-reduced-motion: no video, no butterflies; the poster/plate stands.

const MODE_EVENT = 'anemoia:mode-change';
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
/* How long a loop gets to paint a frame before the poster still steps in. Long
   enough that a warm cache or a quick connection is never interrupted by a
   swap; short enough that nobody sits looking at a bare plate. */
const POSTER_GRACE = 500;
const SOUND_PREF_KEY = 'anemoia:sound';
/** Per-tab playhead per mode, so a track resumes instead of restarting. */
const AUDIO_POS_KEY = 'anemoia:audio-pos';

/**
 * The visitor's explicit choice, or null if they have never touched the toggle.
 * The null case matters: with no choice on record every page — home included —
 * opens silent. Once a choice exists it travels with them across pages.
 * @returns {'on'|'off'|null}
 */
function readSoundPref() {
  try {
    const value = localStorage.getItem(SOUND_PREF_KEY);
    return value === 'on' || value === 'off' ? value : null;
  } catch (error) {
    return null;
  }
}

/**
 * Has the visitor interacted with the site at all this visit? Per-tab, like the
 * audio playhead. It is only a hint that autoplay is likely to be allowed — it
 * cannot grant permission, which browsers scope to a single document.
 */
const GESTURE_KEY = 'anemoia:gestured';

function wasGestured() {
  try {
    return sessionStorage.getItem(GESTURE_KEY) === '1';
  } catch (error) {
    return false;
  }
}

function markGestured() {
  try {
    sessionStorage.setItem(GESTURE_KEY, '1');
  } catch (error) {
    /* storage unavailable; this page still works, the next one just re-waits. */
  }
}

function writeSoundPref(on) {
  try {
    localStorage.setItem(SOUND_PREF_KEY, on ? 'on' : 'off');
  } catch (error) {
    /* storage unavailable (private mode); fall back to per-page state. */
  }
}

/**
 * Whether this connection can afford the environment loop at all.
 *
 * The room loops are multi-megabyte. On a metered or slow connection that is
 * minutes of a near-black plate holding the page, plus a stalling decode — the
 * "black screen then glitch" people report on phones. The plate is a designed
 * fallback, so standing on it is a legitimate outcome, not a degraded one.
 */
function connectionCanAffordVideo(speculative) {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!connection) return true; // Safari: no signal to act on, assume fine
  if (connection.saveData) return false;
  const type = connection.effectiveType || '';
  // A loop the visitor is looking at earns a slow connection's bandwidth; one
  // fetched on the chance they flip modes does not.
  if (speculative) return /^4g$/.test(type) || type === '';
  return !/^(slow-)?2g$/.test(type);
}

class AnemoiaWorld extends HTMLElement {
  connectedCallback() {
    this.reduced = REDUCED_MOTION.matches;
    this.butterflyLayer = this.querySelector('.anemoia-world__butterflies');
    // Map mode -> its environment <video> (may be absent until assets exist).
    this.videos = {};
    this.querySelectorAll('.anemoia-world__env').forEach((env) => {
      const mode = env.dataset.mode;
      const video = env.querySelector('video');
      if (mode && video) this.videos[mode] = video;
    });

    /* A layer with a still but no loop behind it at all (no video setting, no
       MP4 in Files): nothing is ever going to cover that still, so it is the
       room from the first paint rather than a fallback waiting on a grace
       window that would never end. */
    this.querySelectorAll('.anemoia-world__env').forEach((env) => {
      if (!env.querySelector('video') && env.querySelector('.anemoia-world__poster')) {
        env.dataset.poster = 'on';
      }
    });

    // Optional per-mode audio tracks (e.g. maya.mp3 in solar, sapana.mp3 in
    // lunar on the collection room). The active mode's track plays; mode changes
    // crossfade between them. Follows the same sound-toggle / gesture rules.
    this.modeAudio = {};
    this.querySelectorAll('[data-anemoia-mode-audio]').forEach((audio) => {
      const mode = audio.dataset.anemoiaModeAudio;
      if (mode) this.modeAudio[mode] = audio;
    });
    this.hasModeAudio = Object.keys(this.modeAudio).length > 0;

    this.setAttribute('data-live', '');

    /* Reduced motion means no motion — it does not mean no room. Doing no work
       at all only worked while poster stills were configured; none ever were,
       so the "fallback" was a flat gradient, and on the About page (which forces
       the plate near-black) a screen of pure black with the "press any key"
       prompt — baked into the video — never shown. Load the active loop and
       hold it on its first frame instead: the still room these pages always
       needed, and nothing moves. */
    if (this.reduced) {
      this.freezeFirstFrame(this.currentMode());
      this.onMode = () => this.freezeFirstFrame(this.currentMode());
      document.addEventListener(MODE_EVENT, this.onMode);
      return;
    }

    // Audio: videos autoplay muted (browsers block UNmuted autoplay before any
    // user interaction), and every page now opens silent until the visitor asks
    // for sound with the toggle. That choice is remembered across pages
    // (localStorage), so turning it on once keeps it on everywhere — and only
    // the toggle ever writes it. Home used to start its own soundtrack without
    // being asked; it no longer does, so there is nothing to autostart here.
    const storedPref = readSoundPref();
    this.wantSound = storedPref === 'on';
    this.hasGesture = false;
    this.soundBtn = document.querySelector('[data-anemoia-sound]');
    if (this.soundBtn) {
      if (Object.keys(this.videos).length === 0 && !this.hasModeAudio) {
        this.soundBtn.hidden = true; // nothing to toggle
      } else {
        this.onSoundToggle = () => this.toggleSound();
        this.soundBtn.addEventListener('click', this.onSoundToggle);
        this.updateSoundButton();
      }
    }

    /* The gesture, carried forward. Once the visitor has interacted anywhere on
       the site the flag rides along in sessionStorage, so every later page
       starts trying to play immediately instead of sitting on a paused loop
       waiting to be touched again. It does NOT grant autoplay permission —
       browsers scope user activation per document — but Safari usually hands a
       site autoplay once it has been interacted with, and where it does not the
       retry below covers it. */
    if (wasGestured()) this.hasGesture = true;

    // `pointermove` is what makes the unlock effectively instant on desktop. A
    // touch device has none of that, so the touch/click events matter: they are
    // the only ones iOS counts as a user activation for media.
    this.gestureEvents = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'touchend', 'click', 'pointermove'];
    /* Retried, not one-shot. The old handler unbound itself on the FIRST event
       of any kind — and `pointermove` fires on a touch-drag while granting no
       activation at all on iOS, so the one attempt was routinely spent on an
       event that could not start anything, and the loop stayed paused for the
       rest of the page. Now it keeps trying, throttled, until the video (and
       the audio, if wanted) are genuinely running. */
    this.onGesture = () => {
      const now = performance.now();
      if (now - (this.lastKick || 0) < 400) return;
      this.lastKick = now;

      if (this.videoPlaying() && this.audioSettled()) return this.releaseGestures();
      if (!this.hasGesture) this.hasGesture = true;
      markGestured();
      this.applyAudio();
      this.kickVideo();
    };
    this.gestureEvents.forEach((evt) =>
      window.addEventListener(evt, this.onGesture, { passive: true })
    );

    this.onMode = () => this.activate(this.currentMode());
    this.onVisibility = () => {
      const active = this.videos[this.currentMode()];
      if (active) {
        if (document.hidden) active.pause();
        else active.play().catch(() => {});
      }
      if (this.hasModeAudio) {
        if (document.hidden) {
          this.savePositions(); // capture the playhead before pausing
          Object.values(this.modeAudio).forEach((a) => a.pause());
        } else {
          this.crossfadeModeAudio(this.currentMode());
        }
      }
    };
    // `pagehide` is the one unload event that fires reliably on iOS Safari and
    // survives the bfcache, which is exactly the navigation we need to catch.
    this.onPageHide = () => this.savePositions();
    window.addEventListener('pagehide', this.onPageHide);
    // Belt and braces: a hard close or crash skips pagehide entirely.
    if (this.hasModeAudio) {
      this.posTimer = window.setInterval(() => this.savePositions(), 1000);
    }

    document.addEventListener(MODE_EVENT, this.onMode);
    document.addEventListener('visibilitychange', this.onVisibility);

    const boot = () => {
      this.spawnButterflies();
      this.activate(this.currentMode());
      this.warmOtherMode();
    };
    /* Yield to first paint, but only just. The old 1200ms cap was budgeted for
       a poster carrying the screen while the loop arrived; with no posters set
       it was 1.2s of empty plate added to an already multi-megabyte download,
       before the request was even made. */
    if ('requestIdleCallback' in window) {
      requestIdleCallback(boot, { timeout: 250 });
    } else {
      setTimeout(boot, 150);
    }
  }

  disconnectedCallback() {
    document.removeEventListener(MODE_EVENT, this.onMode);
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.soundBtn && this.onSoundToggle) {
      this.soundBtn.removeEventListener('click', this.onSoundToggle);
    }
    this.releaseGestures();
    if (this.onPageHide) window.removeEventListener('pagehide', this.onPageHide);
    if (this.posTimer) window.clearInterval(this.posTimer);
    Object.values(this.posterTimers || {}).forEach((t) => window.clearTimeout(t));
    this.savePositions();
  }

  /** Is the active mode's loop actually running (not merely told to)? */
  videoPlaying() {
    const active = this.videos[this.currentMode()];
    if (!active) return true; // no loop on this page: nothing to wait for
    return !active.paused && !active.ended && active.readyState >= 2;
  }

  /** Nothing left to start on the audio side — either it is off, or it plays. */
  audioSettled() {
    if (!this.hasModeAudio || !this.wantSound) return true;
    const track = this.modeAudio[this.currentMode()];
    return Boolean(track && !track.paused);
  }

  /** Have another go at the active loop, loading it first if it never was. */
  kickVideo() {
    const mode = this.currentMode();
    const active = this.videos[mode];
    if (!active) return;
    if (!active.dataset.loaded) return this.activate(mode); // loads, then plays
    active.play().catch(() => {});
  }

  releaseGestures() {
    if (!this.onGesture) return;
    this.gestureEvents.forEach((evt) => window.removeEventListener(evt, this.onGesture));
    this.onGesture = null;
  }

  toggleSound() {
    this.hasGesture = true; // a click is itself a user gesture
    this.wantSound = !this.wantSound;
    writeSoundPref(this.wantSound); // remember across pages
    this.applyAudio();
    this.updateSoundButton();
    if (this.wantSound) {
      const active = this.videos[this.currentMode()];
      if (active) active.play().catch(() => {});
    }
  }

  updateSoundButton() {
    if (!this.soundBtn) return;
    this.soundBtn.classList.toggle('is-on', this.wantSound);
    this.soundBtn.setAttribute('aria-pressed', String(this.wantSound));
  }

  currentMode() {
    return document.documentElement.getAttribute('data-anemoia-mode') === 'lunar' ? 'lunar' : 'solar';
  }

  /** Attach a lazy env video's real sources. Returns false if it was already done. */
  attachSources(video, preload) {
    if (video.dataset.loaded) return false;
    video.querySelectorAll('source[data-src]').forEach((s) => {
      s.src = s.dataset.src;
    });
    video.dataset.loaded = '1';
    if (preload) video.preload = preload;
    video.load();
    return true;
  }

  /**
   * Reduced motion: paint the active room as a still, never as a movie.
   *
   * A paused <video> shows its first decoded frame, which is the poster these
   * rooms never had. `preload="metadata"` is enough for that in practice while
   * keeping the download to the head of the file; where a browser stops at
   * metadata and decodes nothing, no frame paints and the plate stands — which
   * is exactly the old behaviour, so there is no way for this to be worse.
   */
  freezeFirstFrame(mode) {
    const video = this.videos[mode];
    if (!video) return;
    // No loop to freeze: the poster still is the room, so hand straight over.
    if (!connectionCanAffordVideo()) return this.watchPoster(mode);
    if (this.attachSources(video, 'metadata')) {
      video.addEventListener(
        'loadeddata',
        () => {
          video.pause(); // never autoplay here, whatever the browser decides
          try {
            video.currentTime = 0;
          } catch (error) {
            /* seek unsupported on this source; the decoded first frame stands */
          }
        },
        { once: true }
      );
    }
    // Metadata alone leaves some browsers at readyState 1 with nothing decoded,
    // and there is no second attempt here — so the still matters most on this path.
    this.watchPoster(mode);
  }

  /**
   * Decide whether one mode's poster still is needed at all.
   *
   * The still exists to cover a late loop, not to introduce itself on every
   * visit — so it stays clear through a grace window and only fades in if no
   * real frame has decoded by the end of it. A fast or cached load beats the
   * window every time and shows nothing but the room. Once a frame does arrive
   * the <video> covers the still by itself, so there is no fade-out to time.
   */
  watchPoster(mode) {
    const video = this.videos[mode];
    const env = video && video.closest('.anemoia-world__env');
    if (!env || !env.querySelector('.anemoia-world__poster')) return;

    this.posterTimers = this.posterTimers || {};
    if (this.posterTimers[mode]) return; // already waiting on this one

    const show = () => {
      env.dataset.poster = 'on';
    };

    // No loop is coming (save-data, 2g, or no sources at all): the still is the
    // room, and there is nothing to hold it back for.
    if (!video.dataset.loaded) return show();
    if (video.readyState >= 2) return; // already painted; never show the still

    this.posterTimers[mode] = window.setTimeout(() => {
      if (video.readyState < 2) show();
    }, POSTER_GRACE);

    video.addEventListener(
      'loadeddata',
      () => {
        window.clearTimeout(this.posterTimers[mode]);
        this.posterTimers[mode] = null;
      },
      { once: true }
    );
  }

  /**
   * Once the room on screen is running, quietly fetch the other mode's loop so
   * the SOLAR/LUNAR switch is instant. Before this, a toggle started a
   * multi-megabyte download from cold and the room sat on the bare plate for
   * seconds — the sluggish world transitions people report. Deferred until the
   * active loop is actually playing and gated on the connection, so it never
   * competes with the video the visitor is currently looking at.
   */
  warmOtherMode() {
    const active = this.videos[this.currentMode()];
    if (!active) return;

    const warm = () => {
      if (!connectionCanAffordVideo(true)) return;
      Object.entries(this.videos).forEach(([mode, video]) => {
        if (mode === this.currentMode()) return;
        this.attachSources(video, 'auto');
      });
    };
    const schedule = () => {
      if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 4000 });
      else window.setTimeout(warm, 2000);
    };

    if (!active.paused && active.readyState >= 3) schedule();
    else active.addEventListener('playing', schedule, { once: true });
  }

  // Lazily attach the active mode's sources, play it, pause the others.
  activate(mode) {
    const canLoad = connectionCanAffordVideo();
    Object.entries(this.videos).forEach(([m, video]) => {
      if (m === mode) {
        if (!video.dataset.loaded && !canLoad) return this.watchPoster(m); // still stands in
        // 'auto' drops the markup's preload="none" hint, which otherwise leaves
        // browsers dribbling the fetch of the loop we are about to play.
        this.attachSources(video, 'auto');
        video.play().catch(() => {}); // autoplay may be blocked; poster remains
        this.watchPoster(m);
      } else {
        video.pause();
      }
    });
    this.applyAudio();
  }

  // The active video carries sound once sound is wanted AND a gesture has
  // happened (browsers won't unmute autoplay before then); others stay muted.
  applyAudio() {
    const active = this.currentMode();
    // When per-mode audio tracks exist (collection room), they carry the sound,
    // so keep the background videos muted there to avoid doubling.
    Object.entries(this.videos).forEach(([m, video]) => {
      const on = this.wantSound && this.hasGesture && m === active && !this.hasModeAudio;
      video.muted = !on;
      if (on) video.volume = 1;
    });
    this.crossfadeModeAudio(active);
  }

  /**
   * Plays the active mode's audio track and crossfades the others out, ramping
   * volume so SOLAR (maya) and LUNAR (sapana) blend smoothly on mode change.
   * @param {string} mode
   */
  crossfadeModeAudio(mode) {
    if (!this.hasModeAudio) return;
    const audible = this.wantSound && this.hasGesture;
    Object.entries(this.modeAudio).forEach(([m, audio]) => {
      if (m === mode) {
        // Sound off: pause rather than play muted. A muted-but-playing track
        // would keep advancing its playhead, so the saved position would drift
        // and the resume would land somewhere the visitor never heard.
        if (!audible) {
          this.fadeAudio(audio, 0, 300, () => audio.pause());
          return;
        }
        this.restorePosition(m, audio);
        audio.muted = false;
        audio.play().catch(() => {});
        this.fadeAudio(audio, 1, 900);
      } else {
        // Keep it unmuted while fading so the crossfade is audible, then pause.
        this.fadeAudio(audio, 0, 900, () => audio.pause());
      }
    });
  }

  /* ------------------------------------------------ cross-page continuity -- */
  /*
   * Each page load is a fresh document, so the <audio> would otherwise restart
   * from zero every time. We keep the playhead per mode in sessionStorage
   * (per-tab, cleared when the tab closes) and seek back to it on the next page.
   */

  readPositions() {
    try {
      return JSON.parse(sessionStorage.getItem(AUDIO_POS_KEY) || '{}') || {};
    } catch (error) {
      return {};
    }
  }

  savePositions() {
    if (!this.hasModeAudio) return;
    const pos = this.readPositions();
    let changed = false;
    Object.entries(this.modeAudio).forEach(([m, audio]) => {
      if (audio.currentTime > 0) {
        pos[m] = audio.currentTime;
        changed = true;
      }
    });
    if (!changed) return;
    // Stamped, so the next page can carry the track forward across the gap
    // between documents as well as across the playing time. See restorePosition.
    pos.at = Date.now();
    try {
      sessionStorage.setItem(AUDIO_POS_KEY, JSON.stringify(pos));
    } catch (error) {
      /* storage unavailable; the track simply restarts on the next page. */
    }
  }

  /**
   * Seek a track back to where it left off. `preload="none"` means duration is
   * usually unknown at this point, so the seek waits for metadata. Only ever
   * runs once per element, or a mode toggle would rewind a playing track.
   *
   * The playhead runs on a WALL CLOCK, not just on playing time: the saved
   * position is advanced by however long ago it was written. This is what makes
   * the track carry forward on a phone. Desktop unlocks audio almost instantly
   * (`pointermove` counts as the first gesture), but a touch device has no
   * gesture until the visitor taps — and that tap is usually the one that
   * navigates. Without the clock, every page would start audio, play it for a
   * fraction of a second, save ~0, and the visitor would hear the same opening
   * bars over and over instead of one continuous piece.
   */
  restorePosition(mode, audio) {
    if (audio._resumed) return;
    audio._resumed = true;

    const saved = this.readPositions();
    const from = Number(saved[mode]);
    if (!Number.isFinite(from) || from <= 0) return;

    const at = Number(saved.at);
    const elapsed = Number.isFinite(at) ? Math.max(0, (Date.now() - at) / 1000) : 0;
    const want = from + elapsed;

    const seek = () => {
      const duration = audio.duration;
      // The tracks loop, so wrap rather than clamp — which also keeps a long
      // gap (a visitor who wandered off for ten minutes) somewhere sensible.
      const target = Number.isFinite(duration) && duration > 0 ? want % duration : want;
      try {
        audio.currentTime = target;
      } catch (error) {
        /* seek refused (stream not seekable); it just starts from the top. */
      }
    };

    if (audio.readyState >= 1) seek();
    else audio.addEventListener('loadedmetadata', seek, { once: true });
  }

  /**
   * Ramps an audio element's volume to a target over `duration` ms.
   * @param {HTMLAudioElement} audio
   * @param {number} target 0..1
   * @param {number} duration
   * @param {() => void} [onDone]
   */
  fadeAudio(audio, target, duration, onDone) {
    if (audio._fadeRaf) cancelAnimationFrame(audio._fadeRaf);
    const start = audio.volume;
    if (duration <= 0 || Math.abs(start - target) < 0.01) {
      audio.volume = target;
      audio._fadeRaf = null;
      if (onDone) onDone();
      return;
    }
    const t0 = performance.now();
    const step = (now) => {
      const k = Math.min(1, (now - t0) / duration);
      audio.volume = Math.max(0, Math.min(1, start + (target - start) * k));
      if (k < 1) {
        audio._fadeRaf = requestAnimationFrame(step);
      } else {
        audio._fadeRaf = null;
        if (onDone) onDone();
      }
    };
    audio._fadeRaf = requestAnimationFrame(step);
  }

  spawnButterflies() {
    if (!this.butterflyLayer || this.butterflyLayer.dataset.disabled) return;
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const n = mobile ? 3 : 6;
    // Drifting motes use logo1.png / logo2.png (Settings > Files) when present,
    // alternating between the two; otherwise fall back to the old <333 glyph.
    const logos = [this.butterflyLayer.dataset.logo1, this.butterflyLayer.dataset.logo2].filter(Boolean);
    for (let i = 0; i < n; i++) {
      let b;
      if (logos.length) {
        b = document.createElement('img');
        b.src = logos[i % logos.length];
        b.alt = '';
        b.loading = 'lazy';
        b.decoding = 'async';
        b.className = 'anemoia-butterfly anemoia-butterfly--logo';
      } else {
        b = document.createElement('span');
        b.className = 'anemoia-butterfly';
        b.textContent = '<333';
      }
      b.style.left = `${Math.random() * 90}%`;
      b.style.top = `${10 + Math.random() * 80}%`;
      const dur = 18 + Math.random() * 22;
      b.animate(
        [
          { transform: 'translate(0,0) rotate(0deg)', opacity: 0 },
          { opacity: 0.8, offset: 0.1 },
          { transform: `translate(${30 + Math.random() * 50}px, ${-40 - Math.random() * 60}px) rotate(8deg)`, opacity: 0.8, offset: 0.5 },
          { opacity: 0.8, offset: 0.9 },
          { transform: `translate(${-20 + Math.random() * 80}px, ${20 + Math.random() * 40}px) rotate(-6deg)`, opacity: 0 },
        ],
        { duration: dur * 1000, delay: -Math.random() * dur * 1000, iterations: Infinity, easing: 'ease-in-out' }
      );
      this.butterflyLayer.appendChild(b);
    }
  }
}

if (!customElements.get('anemoia-world')) {
  customElements.define('anemoia-world', AnemoiaWorld);
}
